import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ComprovanteResgateModal from './ComprovanteResgateModal';
import { creditosFinanceiros } from '@/mocks/creditosFinanceiros';

interface Resgate {
  id: string;
  item: string;
  tipo: string;
  pontos: number;
  data: string;
  status: string;
  [key: string]: any;
}

interface MeusResgatesProps {
  resgates: Resgate[];
}

const ITENS_POR_PAGINA_OPCOES = [5, 10, 20];

const statusConfig: Record<string, { cor: string; texto: string; icone: string }> = {
  entregue: { cor: 'bg-green-100 text-green-700', texto: 'Entregue', icone: 'ri-checkbox-circle-fill' },
  concluido: { cor: 'bg-green-100 text-green-700', texto: 'Concluído', icone: 'ri-checkbox-circle-fill' },
  em_transito: { cor: 'bg-teal-100 text-teal-700', texto: 'Em Trânsito', icone: 'ri-truck-line' },
  ativo: { cor: 'bg-amber-100 text-amber-700', texto: 'Ativo', icone: 'ri-play-circle-fill' },
  utilizado: { cor: 'bg-slate-100 text-slate-700', texto: 'Utilizado', icone: 'ri-check-double-line' },
  pendente: { cor: 'bg-yellow-100 text-yellow-700', texto: 'Pendente', icone: 'ri-time-line' }
};

const tipoIcones: Record<string, string> = {
  produto: 'ri-shopping-bag-line',
  financeiro: 'ri-bank-card-line',
  voucher: 'ri-coupon-line',
  curso: 'ri-graduation-cap-line',
  experiencia: 'ri-compass-3-line'
};

const tipoLabels: Record<string, string> = {
  todos: 'Todos',
  produto: 'Produto',
  financeiro: 'Financeiro',
  voucher: 'Voucher',
  curso: 'Curso',
  experiencia: 'Experiência'
};

const statusLabels: Record<string, string> = {
  todos: 'Todos',
  concluido: 'Concluído',
  em_transito: 'Em Trânsito',
  ativo: 'Ativo',
  utilizado: 'Utilizado',
  pendente: 'Pendente'
};

export default function MeusResgates({ resgates }: MeusResgatesProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroCredito, setFiltroCredito] = useState('todos');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [comprovanteResgate, setComprovanteResgate] = useState<any>(null);

  // Lê creditoId da URL e pré-aplica o filtro
  useEffect(() => {
    const creditoIdParam = searchParams.get('creditoId');
    if (creditoIdParam) {
      const existe = creditosFinanceiros.some(c => c.id === creditoIdParam);
      setFiltroCredito(existe ? creditoIdParam : 'todos');
    }
  }, [searchParams]);

  // Créditos que aparecem nos resgates (via campo creditoId ou creditoUtilizado)
  const creditosPresentes = useMemo(() => {
    const ids = new Set(resgates.map(r => r.creditoId || r.creditoUtilizado).filter(Boolean));
    return creditosFinanceiros.filter(c => ids.has(c.id));
  }, [resgates]);

  const resgatesFiltrados = useMemo(() => {
    return resgates.filter(r => {
      const matchTipo = filtroTipo === 'todos' || r.tipo === filtroTipo;
      const matchStatus = filtroStatus === 'todos' || r.status === filtroStatus;
      const matchCredito = filtroCredito === 'todos'
        ? true
        : filtroCredito === 'sem_credito'
          ? !r.creditoId && !r.creditoUtilizado
          : (r.creditoId === filtroCredito || r.creditoUtilizado === filtroCredito);
      const matchBusca = busca.trim() === '' ||
        r.item.toLowerCase().includes(busca.toLowerCase()) ||
        r.tipo.toLowerCase().includes(busca.toLowerCase()) ||
        r.status.toLowerCase().includes(busca.toLowerCase()) ||
        new Date(r.data).toLocaleDateString('pt-BR').includes(busca) ||
        (r.rastreio && r.rastreio.toLowerCase().includes(busca.toLowerCase())) ||
        (r.codigo && r.codigo.toLowerCase().includes(busca.toLowerCase())) ||
        (r.chavePix && r.chavePix.toLowerCase().includes(busca.toLowerCase()));
      return matchTipo && matchStatus && matchCredito && matchBusca;
    });
  }, [resgates, busca, filtroTipo, filtroStatus, filtroCredito]);

  const totalPaginas = Math.max(1, Math.ceil(resgatesFiltrados.length / itensPorPagina));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const indiceInicio = (paginaSegura - 1) * itensPorPagina;
  const resgatesPagina = resgatesFiltrados.slice(indiceInicio, indiceInicio + itensPorPagina);

  const irParaPagina = (p: number) => setPaginaAtual(Math.max(1, Math.min(p, totalPaginas)));

  const limparFiltros = () => {
    setBusca('');
    setFiltroTipo('todos');
    setFiltroStatus('todos');
    setFiltroCredito('todos');
    setPaginaAtual(1);
  };

  const temFiltrosAtivos = busca.trim() !== '' || filtroTipo !== 'todos' || filtroStatus !== 'todos' || filtroCredito !== 'todos';

  const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.pendente;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.cor}`}>
        <i className={config.icone}></i>
        {config.texto}
      </span>
    );
  };

  // Resumo
  const totalPontos = resgates.reduce((acc, r) => acc + r.pontos, 0);
  const concluidos = resgates.filter(r => ['entregue', 'concluido', 'utilizado'].includes(r.status)).length;
  const emTransito = resgates.filter(r => r.status === 'em_transito').length;
  const ativos = resgates.filter(r => r.status === 'ativo').length;

  return (
    <div className="space-y-5">
      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-green-600"></i>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{concluidos}</p>
              <p className="text-xs text-slate-500">Concluídos</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center">
              <i className="ri-truck-line text-teal-600"></i>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{emTransito}</p>
              <p className="text-xs text-slate-500">Em Trânsito</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <i className="ri-play-circle-fill text-amber-600"></i>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{ativos}</p>
              <p className="text-xs text-slate-500">Ativos</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
              <i className="ri-coin-line text-orange-600"></i>
            </div>
            <div>
              <p className="text-xl font-bold text-slate-900">{totalPontos.toLocaleString('pt-BR')}</p>
              <p className="text-xs text-slate-500">Pts Resgatados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de busca e filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        {/* Busca */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className="ri-search-line text-slate-400 text-base"></i>
          </div>
          <input
            type="text"
            value={busca}
            onChange={e => { setBusca(e.target.value); setPaginaAtual(1); }}
            placeholder="Buscar por item, código, chave PIX, rastreio..."
            className="w-full pl-9 pr-9 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
          />
          {busca && (
            <button onClick={() => { setBusca(''); setPaginaAtual(1); }}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer">
              <i className="ri-close-line text-base"></i>
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="space-y-2">
          {/* Tipo */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap w-10">Tipo:</span>
            <div className="flex flex-wrap gap-1">
              {Object.entries(tipoLabels).map(([key, label]) => (
                <button key={key} onClick={() => { setFiltroTipo(key); setPaginaAtual(1); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    filtroTipo === key ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap w-10">Status:</span>
            <div className="flex flex-wrap gap-1 flex-1">
              {Object.entries(statusLabels).map(([key, label]) => (
                <button key={key} onClick={() => { setFiltroStatus(key); setPaginaAtual(1); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    filtroStatus === key ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
            {temFiltrosAtivos && (
              <button onClick={limparFiltros}
                className="text-xs text-orange-500 hover:text-orange-700 font-medium cursor-pointer whitespace-nowrap ml-auto">
                <i className="ri-filter-off-line mr-1"></i>
                Limpar filtros
              </button>
            )}
          </div>

          {/* Crédito */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap w-10">Crédito:</span>
            <div className="flex items-center gap-2 flex-1 flex-wrap">
              <div className="relative min-w-[220px]">
                <select
                  value={filtroCredito}
                  onChange={e => { setFiltroCredito(e.target.value); setPaginaAtual(1); }}
                  className="w-full pl-3 pr-8 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white text-slate-700 cursor-pointer appearance-none font-medium"
                >
                  <option value="todos">Todos os créditos</option>
                  {creditosFinanceiros.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.descricao} — R$ {c.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({c.campanha})
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  <i className="ri-arrow-down-s-line text-slate-400 text-sm"></i>
                </div>
              </div>
              {/* Data do crédito selecionado + link para histórico */}
              {filtroCredito !== 'todos' && (() => {
                const c = creditosFinanceiros.find(x => x.id === filtroCredito);
                if (!c) return null;
                const dataFormatada = new Date(c.dataCredito).toLocaleDateString('pt-BR');
                return (
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <i className="ri-calendar-check-line text-slate-400"></i>
                      Creditado em <strong className="text-slate-700">{dataFormatada}</strong>
                    </span>
                    <button
                      onClick={() => {
                        const dataCredito = new Date(c.dataCredito);
                        navigate(`/premiado/extrato?busca=${encodeURIComponent(dataCredito.toLocaleDateString('pt-BR'))}&tipo=bonus`);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-50 text-teal-700 text-xs font-semibold hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap border border-teal-200"
                    >
                      <i className="ri-external-link-line text-xs"></i>
                      Ver no histórico
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Resultado */}
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            {resgatesFiltrados.length === 0
              ? 'Nenhum resgate encontrado'
              : `${resgatesFiltrados.length} resgate${resgatesFiltrados.length !== 1 ? 's' : ''} encontrado${resgatesFiltrados.length !== 1 ? 's' : ''}`}
          </span>
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap">Por página:</span>
            <select value={itensPorPagina} onChange={e => { setItensPorPagina(Number(e.target.value)); setPaginaAtual(1); }}
              className="px-2 py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-400 cursor-pointer bg-white">
              {ITENS_POR_PAGINA_OPCOES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Tabela */}
      {resgatesFiltrados.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-search-line text-2xl text-slate-400"></i>
          </div>
          <p className="text-slate-600 font-medium mb-1">Nenhum resgate encontrado</p>
          <p className="text-slate-400 text-sm">Tente outros termos ou remova os filtros</p>
          {temFiltrosAtivos && (
            <button onClick={limparFiltros}
              className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
              Limpar filtros
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Item</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Pontos</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Data</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Detalhes</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Comprovante</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {resgatesPagina.map((resgate) => (
                  <tr key={resgate.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${tipoIcones[resgate.tipo] || 'ri-gift-line'} text-orange-600`}></i>
                        </div>
                        <div>
                          <span className="font-medium text-slate-900 text-sm">{resgate.item}</span>
                          {(() => {
                            const creditoId = resgate.creditoId || resgate.creditoUtilizado;
                            const credito = creditoId ? creditosFinanceiros.find(c => c.id === creditoId) : null;
                            if (!credito) return null;
                            const dataCredito = new Date(credito.dataCredito).toLocaleDateString('pt-BR');
                            return (
                              <div className="flex items-center gap-1 mt-1">
                                <i className="ri-medal-line text-teal-500 text-xs"></i>
                                <span className="text-xs text-teal-700 font-medium">{credito.descricao}</span>
                                <span className="text-xs text-slate-400">· {credito.campanha}</span>
                                <span className="text-xs text-slate-400">· {dataCredito}</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="capitalize text-slate-600 text-sm">{tipoLabels[resgate.tipo] || resgate.tipo}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-semibold text-slate-900 text-sm">{resgate.pontos.toLocaleString('pt-BR')}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-slate-600 text-sm">{new Date(resgate.data).toLocaleDateString('pt-BR')}</span>
                    </td>
                    <td className="px-5 py-4">{getStatusBadge(resgate.status)}</td>
                    <td className="px-5 py-4">
                      <div className="text-xs text-slate-600 space-y-0.5">
                        {resgate.tipo === 'produto' && resgate.rastreio && (
                          <>
                            <div className="flex items-center gap-1.5">
                              <i className="ri-truck-line text-teal-600"></i>
                              <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{resgate.rastreio}</span>
                            </div>
                            <div className="text-slate-400">{resgate.transportadora} • Prev: {new Date(resgate.previsaoEntrega).toLocaleDateString('pt-BR')}</div>
                          </>
                        )}
                        {resgate.tipo === 'financeiro' && (
                          <>
                            <div><span className="font-medium">Valor:</span> {resgate.valor}</div>
                            <div className="text-slate-400">Chave: {resgate.chavePix}</div>
                          </>
                        )}
                        {resgate.tipo === 'voucher' && (
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium">Código:</span>
                            <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{resgate.codigo}</span>
                          </div>
                        )}
                        {resgate.tipo === 'curso' && (
                          <>
                            <div className="text-teal-600 font-medium">Acesso liberado</div>
                            <div className="text-slate-400">Válido até: {new Date(resgate.validade).toLocaleDateString('pt-BR')}</div>
                          </>
                        )}
                        {resgate.tipo === 'experiencia' && (
                          <div><span className="font-medium">Local:</span> {resgate.local}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => setComprovanteResgate(resgate)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-file-text-line text-sm"></i>
                        Comprovante
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Exibindo <span className="font-semibold text-slate-900">{indiceInicio + 1}</span>–<span className="font-semibold text-slate-900">{Math.min(indiceInicio + itensPorPagina, resgatesFiltrados.length)}</span> de <span className="font-semibold text-slate-900">{resgatesFiltrados.length}</span>
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => irParaPagina(1)} disabled={paginaSegura === 1}
                  className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors">
                  <i className="ri-skip-back-mini-line text-sm"></i>
                </button>
                <button onClick={() => irParaPagina(paginaSegura - 1)} disabled={paginaSegura === 1}
                  className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors">
                  <i className="ri-arrow-left-s-line text-sm"></i>
                </button>
                {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                  .filter(p => Math.abs(p - paginaSegura) <= 2)
                  .map(p => (
                    <button key={p} onClick={() => irParaPagina(p)}
                      className={`w-7 h-7 flex items-center justify-center rounded text-xs font-medium transition-colors cursor-pointer ${
                        p === paginaSegura ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-slate-200'
                      }`}>
                      {p}
                    </button>
                  ))}
                <button onClick={() => irParaPagina(paginaSegura + 1)} disabled={paginaSegura === totalPaginas}
                  className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors">
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
                <button onClick={() => irParaPagina(totalPaginas)} disabled={paginaSegura === totalPaginas}
                  className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors">
                  <i className="ri-skip-forward-mini-line text-sm"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ComprovanteResgateModal resgate={comprovanteResgate} onClose={() => setComprovanteResgate(null)} />
    </div>
  );
}
